import { useEffect, useState } from 'react';
import { Paper, InputBase, IconButton } from '@mui/material';
import { IconDownload, IconSearch, IconUpload } from '../../../icons/Icon';
import UploadModal from '../../../components/commonComponent/UploadModal';
import HolidayCalendarTable from '../../../components/commonComponent/HolidayCalendarTable';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../api/axios';
import { BASE_URL } from '../../../constants/apiConstants';
import { toast } from 'react-toastify';
import { formatISO, isValid, addDays, format, parse } from 'date-fns'
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/store';
import { fetchAllSites } from '../../../services/sectorRegionAllSitesSlice';

const HolidayCalendarDetails = () => {
  const [open, setOpen] = useState(false); // Modal open state
  const { sites } = useSelector((state: any) => state.sectorRegionAllSites);
  const [searchQuery, setSearchQuery] = useState<string | null>(null); // Search query state
  const [excelData, setExcelData] = useState<any[]>([]); // Excel data
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const userId = localStorage.getItem('userid');
  const dispatch = useDispatch<AppDispatch>();

  const expectedColumns = ['S. No.', 'Holiday Name', 'Date'];
  // Function to close modal
  const handleClose = () => {
    setOpen(false);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, selectedSite: string | null) => {
    setSelectedSite(selectedSite);
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const binaryStr = e.target?.result as string;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData: [][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      // Remove empty rows
      const filteredData = jsonData.filter((row: any[]) => {
        return row.some((cell) => cell !== null && cell !== undefined && cell !== '');
      });
      const columns: any[] = filteredData[0] as any[]; // Header row
      const isValidExcel = expectedColumns.every((col) => columns.includes(col));
      if (isValidExcel) {
        const fixedData = filteredData.slice(1).map((row: any[]) => {
          return row.map((cell, index) => {
            // Check if the current column is "S. No." (index 0)
            // if (index === 0 && typeof cell !== 'number') {
            //   toast(`Invalid S. No. at row ${row[0]}, it should be a number.`, { type: "error" });
            //   return null;
            // }  
            // Check if the current column is "Holiday Name" (index 1)
            if (index === 1 && /[^a-zA-Z0-9\s]/.test(cell)) {
              toast(`Holiday Name contains special characters at row ${row[0]}`, { type: "error" });
              return null;
            }
            // Check if the current column is the "Date" column (index 2)
            if (index === 2 && typeof cell === 'number') {
              // Convert Excel serial number to a proper date
              const baseDate = new Date(1899, 11, 30); // Excel's base date
              const parsedDate = addDays(baseDate, cell);
              return isValid(parsedDate) ? format(parsedDate, 'dd-MM-yyyy') : cell;
            }
            return cell; // For other cells, keep the value as is
          });
        });
        // Filter out rows with invalid "S. No." or "Holiday Name"
        const validData = fixedData.filter((row) => row && row.every((cell) => cell !== null));
        setExcelData(validData); // Set fixed data without the header
        setOpen(false);
      } else {
        setExcelData([]);
        toast("Invalid template. Please upload a file with the correct columns.", { type: "error" });
      }
    };
    reader.readAsBinaryString(file);
  };


  useEffect(() => {
    if (excelData.length > 0) {
      updateSaveAllSiteWiseHolidays();
    }

  }, [excelData]);

  // Function to update the site wise holidays
  const updateSaveAllSiteWiseHolidays = async () => {
    const payload = {
      userid: userId,
      sectorcode: sites[0].sectorcode,  // Single sectorcode from the first site
      regionid: sites[0].regionid,  // Single regionid from the first site
      isallsites: selectedSite ? false : true,
      sitecode: selectedSite ? selectedSite : '',
      holidaylist: excelData.map((holiday) => {
        const excelDate = holiday[2];
        const regex = /^\d{2}-\d{2}-\d{4}$/;
        if (regex.test(excelDate)) {
          const parsedDate = parse(excelDate, 'dd-MM-yyyy', new Date());
          if (!isValid(parsedDate)) {
            toast(`Invalid date for ${holiday[1]}, please enter date in dd-mm-yyyy format`, { type: "error" });
          }
          else {
            return {
              holidayname: holiday[1],
              holidaydate: formatISO(new Date(parsedDate)),
            };
          }
        }

      }),
    };

    try {
      await axiosInstance.post(`${BASE_URL}/api/v1/AdminConfiguration/SaveSiteWiseHolidays`, payload);
      dispatch(fetchAllSites({
        sector: sites[0].sectorcode,
        region: sites[0].regionid
      }));
      toast("Holiday calendar saved successfully", { type: "success" });
    } catch (err) {
      console.log(err)
      toast("Failed to save holiday calendar", { type: "error" });
    }
  };

  // Function to download the template
  const handleDownloadTemplate = () => {
    const data = [
      ['S. No.', 'Holiday Name', 'Date'],
    ];
    // Create a worksheet from the data
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Holiday Calendar');
    XLSX.writeFile(wb, 'holiday_calendar_template.xlsx');
  };

  // Filtering the sites based on the search query
  const filteredSites = sites.filter((site: any) => {
    const searchLower = (searchQuery ?? '').toLowerCase().trim();
    const siteName = site.sitename ? site.sitename.toLowerCase() : '';
    const siteCode = site.sitecode ? site.sitecode.toLowerCase() : '';
    return siteName.includes(searchLower) || siteCode.includes(searchLower);
  });

  return (
    <div className="white-box mb-12">
      <div className="flex justify-content-between align-items-center title-button-wrapper mb-8">
        <div className="flex align-items-center">
          <Paper component="form" className="search-input mr-12">
            <InputBase placeholder="Search Sites / Site Code" onChange={(e) => setSearchQuery(e?.target?.value)} onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()} />
            <IconButton type="button" aria-label="search">
              <IconSearch />
            </IconButton>
          </Paper>
        </div>
        <div className="button-wrap flex align-items-center">
          <button className="cmenu-btn" onClick={handleDownloadTemplate}>
            <IconDownload /> Download Template
          </button>
          <span className="divider"></span>
          <button className="cmenu-btn disabled" onClick={() => setOpen(true)} disabled={sites.length === 0}>
            <IconUpload /> Upload Holiday Calendar For All Sites
          </button>
        </div>
      </div>

      <div className="inner-contant kitchen-dashboard">
        <HolidayCalendarTable sites={filteredSites} excelData={excelData} handleUpload={handleFileUpload} />
      </div>

      {open && (
        <UploadModal
          open={open}
          title="Upload Holiday Calendar for All Sites"
          discription="Please note that once the Holiday Calendar is uploaded for All Sites, it will be synchronized and replicated across all locations simultaneously, ensuring consistent application of holiday schedules."
          handleClose={handleClose}
          handleFileUpload={(e) => { handleFileUpload(e, null) }}
        />
      )}
    </div>
  );
};

export default HolidayCalendarDetails;
